import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Box } from '@mui/material'
import { toast } from 'react-toastify'
import { useLocation, useOutletContext } from 'react-router-dom'
import { editObjectFilter, getObjectsWithFilter } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import Form from '../../form/Form'
import Toolbar from '../../toolbar/Toolbar'
import { getSettingsFields, getSettingsTabs } from './SettingsFields'

const Settings = ({
  modeProps = 'normal',
  setStepFailed,
  settingsAdd,
  setSettingsAdd,
  check,
  setErrorsSettings,
  setCheck,
  onValidStep
}) => {
  const [settings, setSettings] = useState({})
  const [mode, setMode] = useState(null)
  const [isSubmit, setIsSubmit] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const toastId = useRef(null)

  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const userRight = useOutletContext()

  const memoizedTabs = useMemo(() => getSettingsTabs(), [])
  const memoizedFields = useMemo(() => getSettingsFields(user), [user])

  const options = useMemo(
    () => ({
      tabs: memoizedTabs,
      fields: memoizedFields,
      tabDefault: '1',
      data: settings,
      location
    }),
    [settings, location]
  )

  useEffect(() => {
    if (modeProps === 'wizard') {
      setSettings(settingsAdd)
    }
  }, [settingsAdd, modeProps])

  useEffect(() => {
    if (modeProps !== 'wizard') return

    setErrorsSettings(isFormError)
    setStepFailed(isFormError)
  }, [isFormError, modeProps])

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getObjectsWithFilter('settings', { company_id: user.company_id })
        setSettings(res.data)
      } catch (err) {}
    }

    if (modeProps !== 'normal') {
      setSettings(settingsAdd)
      return
    }

    if (location.pathname === '/settings/edit') {
      setMode('edit')
      fetchSettings()
    } else if (location.pathname === '/settings') {
      setMode('view')
      fetchSettings()
    }
  }, [location.pathname, user, modeProps])

  useEffect(() => {
    if (!isSubmit) return

    const submitSettings = async () => {
      if (modeProps === 'normal') {
        if (location.pathname === '/settings/edit') {
          toastId.current = toast.loading('Enregistrement en cours')

          try {
            const res = await editObjectFilter(
              'settings',
              { company_id: user.company_id },
              { ...settings, lastModBy: user.email }
            )

            toast.update(toastId.current, {
              type: 'success',
              render: 'Enregistrement terminé',
              autoClose: 2000,
              isLoading: false
            })
            navigate('/settings')
          } catch (e) {
            toast.update(toastId.current, {
              type: 'error',
              render: e.response?.statusText || e.message,
              autoClose: 2000,
              isLoading: false
            })
          }
        }
      } else if (modeProps === 'wizard') {
        setSettingsAdd((prev) => ({
          ...prev,
          ...settings
        }))
        setCheck(false)
        setErrorsSettings(false)
      }

      setIsSubmit(false)
    }

    submitSettings()
  }, [isSubmit, settings, navigate, modeProps, user, setSettingsAdd, setCheck, setErrorsSettings])

  const toolbarOptions = {
    accessRight: userRight,
    objectType: 'settings',
    title: 'Paramètres',
    actions: { edit: true, audits: true }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flex: 1
      }}
    >
      {modeProps === 'normal' && <Toolbar options={toolbarOptions} object={settings} />}
      <Form
        modeProps={modeProps}
        checkForm={check}
        setCheckForm={setCheck}
        mode={mode}
        setFormError={setIsFormError}
        options={options}
        setDataParent={(data) => {
          setSettings(data)
          if (modeProps === 'wizard') {
            setSettingsAdd(data)
          }
        }}
        setIsSubmitParent={setIsSubmit}
        onValidStep={onValidStep}
      />
    </Box>
  )
}

export default Settings
